<template>
	<div class="dorm-floor">
		<div class="container_box" v-if="!loading && floorList.length">
			<div class="rooms">
				<div
					v-for="(item, index) in floorList"
					:key="'f-' + index"
					class="floor-tab"
					:class="{ active: floor === index }"
					@click="switchFloor(index)"
				>{{ index + 1 }}层</div>
			</div>
			<div class="floors" ref="floorContainer">
				<div class="floors-track" ref="floorTrack">
					<div class="floor" v-for="(room, index) in (floorList[floor] || [])" :key="'r-' + index">
						<div class="name">{{ room.name }}</div>
						<div class="stus">
							<div
								class="stu"
								v-for="(stu, idx) in (room.list || [])"
								:key="'s-' + idx"
								:style="{ background: getStatusColor(stu) }"
							>
								<div class="stu-name" :title="stu.name">{{ stu.name }}</div>
								<div class="status" :title="statusTitle(stu)">
									<span v-if="stu.BedNum || stu.bedNum">{{ stu.BedNum || stu.bedNum }} | </span>
									<span>{{ getStatusText(stu) }}</span>
								</div>
								<div class="time" v-if="stu.LastRegTime || stu.lastRegTime || stu.time">
									{{ formatTime(stu.LastRegTime || stu.lastRegTime || stu.time) }}
								</div>
							</div>
						</div>
					</div>
					<div class="un" v-if="!(floorList[floor] && floorList[floor].length)">暂无数据</div>
				</div>
			</div>
			<div class="status-explanation">
				<div class="status-item" v-for="(item, index) in statusLegend" :key="index">
					<div class="status-color" :style="{ backgroundColor: item.color }"></div>
					<div class="status-text">{{ item.text }}</div>
				</div>
			</div>
		</div>
		<div class="container_box un" v-else-if="!loading">暂无数据</div>
		<div class="container_box un" v-else>加载中...</div>
	</div>
</template>

<script>
import { formateDate } from './dormScreenFormat'

export default {
	name: 'DormFloorGrid',
	props: {
		category1: { type: Object, default: null },
	},
	data() {
		return {
			floor: 0,
			carouselTimer: null,
			scrollTimer: null,
			rafId: null,
			/** 楼层结构指纹：轮询刷新时结构未变则不打断当前滚动 */
			floorSig: '',
			/** 高铁站式慢速（约 36px/秒），保证能看清学生卡片 */
			scrollSpeedPx: 0.036,
			/** 滚到顶/底后停留再切层 */
			dwellTopMs: 3000,
			dwellBottomMs: 5000,
			/** 本层内容放得下、无需滚动时的停留 */
			dwellNoScrollMs: 10000,
		}
	},
	computed: {
		loading() {
			return !this.category1
		},
		floorList() {
			return (this.category1 && this.category1.list) || []
		},
		status() {
			return (this.category1 && this.category1.status) || []
		},
		statusMap() {
			const map = new Map()
			this.status.forEach((item) => {
				if (item.name) map.set(item.name, item)
				if (item.code) map.set(item.code, item)
				if (item.code === 'DONE' || item.name === '离寝归寝均正常' || item.name === '已完成') {
					map.set('正常', item)
				}
				if (item.code === 'ISSUE' || item.name === '有问题') {
					map.set('异常', item)
				}
			})
			return map
		},
		statusLegend() {
			if (this.status.length) {
				return this.status.map((item) => {
					let text = item.name || item.code
					if (text === '离寝归寝均正常' || text === '已完成') text = '正常'
					if (text === '有问题') text = '异常'
					return { color: item.color, text }
				})
			}
			return [
				{ color: '#4CAF50', text: '在寝' },
				{ color: '#FFC107', text: '出寝' },
				{ color: '#2196F3', text: '正常' },
				{ color: '#F44336', text: '异常' },
				{ color: '#9C27B0', text: '请假' },
				{ color: '#FF9800', text: '未判定' },
				{ color: '#607D8B', text: '未入住' },
			]
		},
	},
	watch: {
		category1() {
			if (this.floor >= this.floorList.length) this.floor = 0
			// 仅宿舍结构变化时重启轮播；状态文案变化交给 Vue 就地更新
			this.$nextTick(() => this.startCarousel(false))
		},
	},
	mounted() {
		this.startCarousel(true)
	},
	beforeDestroy() {
		this.stopCarousel()
	},
	methods: {
		formatTime(val) {
			return formateDate('HH:MM:SS', val)
		},
		statusTitle(stu) {
			const bed = stu.BedNum || stu.bedNum
			return (bed ? bed + ' | ' : '') + this.getStatusText(stu)
		},
		floorStructureSig() {
			return this.floorList
				.map((rooms, fi) => {
					const roomPart = (rooms || [])
						.map((r) => `${r.name || ''}:${((r && r.list) || []).length}`)
						.join(',')
					return `${fi}:${roomPart}`
				})
				.join('|')
		},
		switchFloor(index) {
			this.floor = index
			this.startCarousel(true)
		},
		stopCarousel() {
			if (this.carouselTimer) {
				clearTimeout(this.carouselTimer)
				this.carouselTimer = null
			}
			if (this.scrollTimer) {
				clearTimeout(this.scrollTimer)
				this.scrollTimer = null
			}
			if (this.rafId != null) {
				cancelAnimationFrame(this.rafId)
				this.rafId = null
			}
		},
		/**
		 * 流程：定位顶部 → 停留 → 滚完本层 → 停留 → 切下一层。
		 * @param {boolean} force 强制重启（切层 / 挂载）
		 */
		startCarousel(force) {
			const sig = this.floorStructureSig()
			if (!force && sig === this.floorSig && (this.rafId != null || this.carouselTimer || this.scrollTimer)) {
				return
			}
			this.floorSig = sig

			this.stopCarousel()
			if (!this.floorList.length) return
			// 等楼层 DOM 画完再量高度，避免未滚完就切层
			this.$nextTick(() => {
				this.scrollTimer = setTimeout(() => {
					const track = this.$refs.floorTrack
					if (!track) return
					track.style.transform = 'translate3d(0,0,0)'
					this.carouselTimer = setTimeout(() => {
						this.scrollCurrentFloorToEnd()
					}, this.dwellTopMs)
				}, 80)
			})
		},
		scrollCurrentFloorToEnd() {
			const el = this.$refs.floorContainer
			const track = this.$refs.floorTrack
			if (!el || !track || !this.floorList.length) return
			const max = Math.max(0, track.offsetHeight - el.clientHeight)
			// 本层无需滚动：看一会再切层
			if (max <= 4) {
				this.carouselTimer = setTimeout(() => this.nextFloor(), this.dwellNoScrollMs)
				return
			}

			let last = performance.now()
			let offset = 0
			const tick = (now) => {
				const elNow = this.$refs.floorContainer
				const trackNow = this.$refs.floorTrack
				if (!elNow || !trackNow) {
					this.rafId = null
					return
				}
				const maxNow = Math.max(0, trackNow.offsetHeight - elNow.clientHeight)
				const dt = Math.min(now - last, 40)
				last = now
				// transform 亚像素位移，避免 scrollTop 整数跳变发虚/顿挫
				offset = Math.min(offset + this.scrollSpeedPx * dt, maxNow)
				trackNow.style.transform = `translate3d(0,${-offset}px,0)`
				if (offset >= maxNow - 0.5) {
					this.rafId = null
					trackNow.style.transform = `translate3d(0,${-maxNow}px,0)`
					this.carouselTimer = setTimeout(() => this.nextFloor(), this.dwellBottomMs)
					return
				}
				this.rafId = requestAnimationFrame(tick)
			}
			this.rafId = requestAnimationFrame(tick)
		},
		nextFloor() {
			if (!this.floorList.length) return
			this.floor = (this.floor + 1) % this.floorList.length
			this.startCarousel(true)
		},
		getStatusText(item) {
			const attMap = {
				NOT_CHECKED_IN: '未入住',
				ISSUE: '异常',
				LEAVE: '请假',
				UNJUDGED: '未判定',
				DONE: '正常',
				NOT_APPLICABLE: '不适用',
			}
			if (item.attendanceStatus && attMap[item.attendanceStatus]) {
				if (this.statusMap.has(item.attendanceStatus)) {
					let name = this.statusMap.get(item.attendanceStatus).name
					if (name === '已完成' || name === '离寝归寝均正常') return '正常'
					if (name === '有问题') return '异常'
					return name
				}
				return attMap[item.attendanceStatus]
			}
			const posMap = { IN_DORM: '在寝', OUT_DORM: '出寝', UNKNOWN: '未知', HISTORY: '历史' }
			if (item.presenceStatus && posMap[item.presenceStatus]) {
				if (this.statusMap.has(item.presenceStatus)) {
					return this.statusMap.get(item.presenceStatus).name
				}
				return posMap[item.presenceStatus]
			}
			if (item.displayStatus) {
				let txt = String(item.displayStatus).trim()
				if (txt === '已完成' || txt === '离寝归寝均正常') return '正常'
				if (txt === '有问题') return '异常'
				return txt
			}
			if (item.status) {
				if (item.status === '已完成' || item.status === '离寝归寝均正常') return '正常'
				if (item.status === '有问题' || item.status === '晚出') return '异常'
				return item.status
			}
			return '未知'
		},
		getStatusColor(item) {
			const code = item.attendanceStatus || item.presenceStatus || item.result
			if (code && this.statusMap.has(code)) return this.statusMap.get(code).color
			const statusText = this.getStatusText(item)
			if (this.statusMap.has(statusText)) return this.statusMap.get(statusText).color
			switch (statusText) {
				case '正常':
				case '已完成':
					return '#2196F3'
				case '异常':
				case '晚归':
				case '晚离':
				case '未离寝':
				case '未归寝':
					return '#F44336'
				case '请假':
					return '#9C27B0'
				case '未判定':
				case '无通行':
					return '#FF9800'
				case '未入住':
					return '#607D8B'
				case '在寝':
					return '#4CAF50'
				case '出寝':
				case '离寝':
					return '#FFC107'
				default:
					return '#2b6bf3'
			}
		},
	},
}
</script>

<style scoped>
.dorm-floor {
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	border: 1px solid rgba(45, 118, 252, 0.45);
	background: rgba(8, 18, 48, 0.55);
	position: relative;
	overflow: hidden;
}
.container_box {
	width: 100%;
	height: 100%;
	display: flex;
	box-sizing: border-box;
	padding: 0.3vw;
	padding-bottom: 2.6vw;
	overflow: hidden;
}
.rooms {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 6.25vw;
	height: 100%;
	background: rgba(30, 53, 193, 0.1);
	overflow: hidden;
}
.floor-tab {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 4.7vw;
	height: 2.6vw;
	font-size: 0.94vw;
	background: rgba(30, 53, 193, 0.5);
	transform: skewX(-12deg);
	margin-top: 0.78vw;
	cursor: pointer;
}
.floor-tab.active {
	background: #2b6bf3;
}
.floors {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	padding: 1vw;
}
.floors-track {
	display: flex;
	flex-wrap: wrap;
	align-content: flex-start;
	justify-content: space-between;
	width: 100%;
	will-change: transform;
	backface-visibility: hidden;
}
.floor {
	width: calc(50% - 0.26vw);
	padding: 0.52vw;
	padding-bottom: 0;
	border-radius: 0.42vw;
	margin-bottom: 0.52vw;
	background: rgba(43, 107, 243, 0.1);
	box-sizing: border-box;
}
.floor > .name {
	display: flex;
	align-items: center;
	width: 100%;
	font-size: 1.04vw;
	margin-bottom: 0.52vw;
}
.floor > .name::before {
	content: '';
	display: block;
	width: 0.1vw;
	height: 1.3vw;
	border-radius: 0.4vw;
	margin-right: 0.52vw;
	background: #2b6bf3;
}
.stus {
	display: flex;
	flex-wrap: wrap;
	width: 100%;
}
.stu {
	width: calc(25% - 0.2vw);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border-radius: 0.2vw;
	padding: 0.26vw;
	margin-right: 0.26vw;
	margin-bottom: 0.52vw;
	box-sizing: border-box;
}
.stu:nth-child(4n) {
	margin-right: 0;
}
.stu-name,
.status,
.time {
	font-size: 0.73vw;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
	text-align: center;
}
.stu-name {
	font-size: 0.73vw;
}
.un {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #94a3b8;
	font-size: 1vw;
}
.status-explanation {
	position: absolute;
	left: 1vw;
	right: 1vw;
	bottom: 0.3vw;
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	gap: 1vw;
	background: rgba(30, 53, 193, 0.15);
	padding: 0.4vw;
	border-radius: 0.4vw;
}
.status-item {
	display: flex;
	align-items: center;
	gap: 0.3vw;
}
.status-color {
	width: 1vw;
	height: 1vw;
	border-radius: 0.2vw;
}
.status-text {
	font-size: 0.73vw;
	color: #fff;
}
</style>
